import { request } from "@umijs/max";
import { abortControllers, AbortControllerType } from "./constants";
import {
  WhatsAppConversationDataType,
  WhatsAppContact,
  WHATSAPP_WS_REQUEST_TYPE,
  ChannelTypeEnum,
  WhatsAppMessageType,
} from "@packages/chat-sdk";

/**
 * 获取置顶会话列表
 *
 */
export type GetTopConversationListParams = {
  fromUserId?: string;
  teamId?: string;
  name?: string;
  unRead?: boolean;
  robotStatus?: boolean;
};
export function getConversationTopListService(
  params: GetTopConversationListParams
) {
  const controller = new AbortController();
  const oldController = abortControllers.get(
    AbortControllerType.CONVERSATION_TOP_LIST
  );
  if (oldController) {
    oldController.abort();
  }
  abortControllers.set(AbortControllerType.CONVERSATION_TOP_LIST, controller);

  return request<API.BaseResponse<WhatsAppConversationDataType[]>>(
    "/api/damselfish-chat-bot/conversation/topList",
    {
      method: "GET",
      signal: controller.signal,
      params: {
        ...params,
        channelType: ChannelTypeEnum.WhatsApp,
      },
    }
  );
}

/**
 * 获取会话列表
 *
 */

export type GetConversationListParams = {
  fromUserId?: string;
  teamId?: string;
  name?: string;
  unRead?: boolean;
  pageNo?: number;
  pageSize?: number;
  robotStatus?: boolean;
};

export function getConversationListService(params: GetConversationListParams) {
  const controller = new AbortController();
  const oldController = abortControllers.get(
    AbortControllerType.CONVERSATION_LIST
  );
  if (oldController) {
    oldController.abort();
  }
  abortControllers.set(AbortControllerType.CONVERSATION_LIST, controller);
  return request<
    API.BaseResponse<{ list: WhatsAppConversationDataType[]; total: number }>
  >("/api/damselfish-chat-bot/conversation/page", {
    method: "GET",
    signal: controller.signal,
    params: {
      ...params,
      channelType: ChannelTypeEnum.WhatsApp,
    },
  });
}

/**
 * 一键清除未读消息
 * */
export function clearUnreadService(teamId?: string) {
  return request<API.BaseResponse<boolean>>(
    "/api/damselfish-chat-bot/conversation/unread",
    {
      method: "POST",
      params: {
        teamId,
        channelType: ChannelTypeEnum.WhatsApp,
      },
    }
  );
}

/**
 *
 * 更改会话状态
 */
type ToggleConversationStatusParams = {
  chat_id: string;
  is_top?: number;
  is_shield?: number;
  unread?: number;
};
type ToggleConversationStatusData = {
  data: {
    chat_id: string;
    is_top?: number;
    is_shield?: number;
    unread?: number;
  };
};

export function toggleConversationStatusService(
  fromUserId: string,
  params: ToggleConversationStatusParams
) {
  return request<API.BaseResponse<ToggleConversationStatusData>>(
    "/api/damselfish-chat-bot/command?UPDATE_CONVERSATION_STATUS",
    {
      method: "POST",
      data: {
        message_type: WHATSAPP_WS_REQUEST_TYPE.UPDATE_CONVERSATION_STATUS,
        data: params,
        user_id: fromUserId,
        channel_type: ChannelTypeEnum.WhatsApp,
      },
    }
  );
}

/**
 * 创建会话
 *
 */
type CreateConversationParams = {
  userId: string;
  otherId: string;
};
export function createConversationService(params: CreateConversationParams) {
  return request<API.BaseResponse<WhatsAppConversationDataType>>(
    "/api/damselfish-chat-bot/conversation/create",
    {
      method: "POST",
      data: {
        ...params,
        channelType: ChannelTypeEnum.WhatsApp,
      },
    }
  );
}

/**
 * 查询会话
 */
type FindConversationParams = {
  userId: string;
  conversationId: string;
};
export function findConversationService(params: FindConversationParams) {
  return request<API.BaseResponse<WhatsAppConversationDataType>>(
    "/api/damselfish-chat-bot/conversation/findById",
    {
      method: "POST",
      data: {
        ...params,
        channelType: ChannelTypeEnum.WhatsApp,
      },
    }
  );
}

/**
 * 查询联系人详情
 */
export function getContactDetailService(userId: string, fromUserId?: string) {
  const controller = new AbortController();
  const oldController = abortControllers.get(
    AbortControllerType.CONTACT_DETAIL
  );
  if (oldController) {
    oldController.abort();
  }
  abortControllers.set(AbortControllerType.CONTACT_DETAIL, controller);
  return request<API.BaseResponse<WhatsAppContact>>(
    "/api/damselfish-chat-bot/user/detail",
    {
      method: "GET",
      signal: controller.signal,
      params: {
        userId,
        fromUserId,
        channelType: ChannelTypeEnum.WhatsApp,
      },
    }
  );
}

/**
 *
 * 获取历史消息
 */
export function getHistoryMessageListService(data: {
  chatId: string;
  lastMsgTime?: string;
  pageNo?: number;
  pageSize?: number;
}) {
  const controller = new AbortController();
  const oldController = abortControllers.get(
    AbortControllerType.MESSAGE_HISTORY
  );
  if (oldController) {
    oldController.abort();
  }
  abortControllers.set(AbortControllerType.MESSAGE_HISTORY, controller);
  return request<
    API.BaseResponse<{
      records: WhatsAppMessageType[];
      total: number;
    }>
  >("/api/damselfish-chat-bot/history/page", {
    method: "POST",
    signal: controller.signal,
    data: {
      ...data,
      channelType: ChannelTypeEnum.WhatsApp,
    },
  });
}

export enum KnowledgeSourceTypeEnum {
  AI = "AI",
  RG_REPLY = "RG_REPLY",
}
export enum KnowledgeRectifyTypeEnum {
  POSITIVE = "POSITIVE",
  NEGATIVE = "NEGATIVE",
}
export type AddKnowledgeDto = {
  sourceType: KnowledgeSourceTypeEnum;
  reply: string;
  question: string;
  rectifyType: KnowledgeRectifyTypeEnum;
  robotId: string;
  msgId: string;
  taskId: string;
  chatId: string;
};
export function addKnowledgeService(data: Partial<AddKnowledgeDto>) {
  return request<API.BaseResponse<Record<string, string>>>(
    "/api/damselfish-chat-bot/history/msgOperate",
    {
      method: "POST",
      data,
    }
  );
}

export function cancelKnowledgeService(
  params: Partial<{
    robotTaskId: string;
  }>
) {
  return request<API.BaseResponse<string[]>>(
    "/api/damselfish-chat-bot/history/removeMsgOperate",
    {
      method: "POST",
      params,
    }
  );
}
